<template>
	<div
		class="column-mapping-editor display-flex"
		:class="{
			'is-merging': isMergeMode && !isMergedHeader,
			'is-being-merged': isColumnnBeingMerged,
			'is-merged-header': isMergedHeader,
		}"
		@mouseover="setHovered(header)"
		@mouseleave="setHovered(null)"
	>
		<div class="column-source arrow-right flex-1 flex-space-between--center ml-2">
			<div class="text-overflow-ellipsis">{{ header }}</div>
			<div class="merged-text font-size-sm ml-3" v-if="isMergedHeader">Merged</div>
		</div>
		<div class="column-destination flex-1 flex-centerize" :class="{ 'is-not-mapped-header': isNotMappedHeader }">
			<div
				v-if="isMergeMode && !isMergedHeader"
				class="display-flex flex-1"
				:class="isColumnnBeingMerged ? 'flex-start--center' : 'flex-end--center'"
			>
				<div v-if="isColumnnBeingMerged" class="flex-1 flex-space-between--center">
					<div class="">Merge with...</div>
					<AppCloseX class="close-icon" @onClose="cancelMerge" />
				</div>
				<CTAButton v-else class="" x-small @click="$emit('onMergeColumn', header)">Merge</CTAButton>
			</div>
			<div v-else class="select-container" :class="{ [selectId]: true }">
				<AppSelect title="" naked dense v-model="selectionLocal" :items="[...columnOptionsLocal]" :attach="`.${selectId}`" />
			</div>
		</div>
	</div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import ColumnMapperService from './ColumnMapperService';
const { MERGE_SELECT_ITEM, SPLIT_SELECT_ITEM, DONT_MAP_SELECT_ITEM } = ColumnMapperService;

export default {
	name: 'ColumnMappingEditor',
	props: {
		header: { type: String },
		columnOptions: { type: Array },
		selection: { type: String },
		isMergeMode: { type: Boolean },
		mergedColumnName: { type: String },
	},
	computed: {
		selectionLocal: {
			get() {
				return this.selection ? this.selection : this.columnOptions[0];
			},
			set(newSelection) {
				if (newSelection === MERGE_SELECT_ITEM.value) {
					this.$emit('setMergeHeaders', this.header);
				} else if (newSelection === SPLIT_SELECT_ITEM.value) {
					this.$emit('splitMergedHeader', this.header);
				} else {
					this.onMappingUpdated(newSelection);
				}
			},
		},
		isColumnnBeingMerged() {
			return this.header === this.mergedColumnName;
		},
		columnOptionsLocal() {
			return this.columnOptions.map((opt) => ({ ...opt, ...{ text: opt.text + (opt.required ? ' (required)' : '') } }));
		},
		isMergedHeader() {
			return !!this.columnOptions.find((opt) => opt.value === SPLIT_SELECT_ITEM.value);
		},
		isNotMappedHeader() {
			return this.selection === undefined || this.selection === DONT_MAP_SELECT_ITEM.value;
		},
	},
	methods: {
		onMappingUpdated(newSelection) {
			this.$emit('onMappingUpdated', this.header, newSelection);
		},
		setHovered(header) {
			this.$emit('setMappingEditorHoveredColumn', header);
		},
		cancelMerge() {
			this.$emit('cancelMerge');
		},
	},
	data() {
		return { selectId: 'select_id-' + uuid() };
	},
};
</script>

<style scoped lang="scss">
.column-mapping-editor {
	$bg-color: #fbfbfd;
	$dest-bg-color: #fff;
	$border-color: var(--border-color);
	$blue: var(--primary-color);
	$merge-cell-bg: var(--app-selection-hover-background);

	width: 100%;
	border: 1px solid $border-color;
	border-radius: 9px;
	background-color: $bg-color;

	.column-source {
		position: relative;
		padding: 8px 40px 8px 4px;
		font-size: 14px;
		font-weight: 400;
		overflow: hidden;

		.merged-text {
			color: #3cc5aa;
			z-index: 9999;
		}
	}
	.column-destination {
		position: relative;
		background-color: transparent;
		padding: 0 4px;
		font-size: 12px;

		button {
			height: 30px;
		}

		&.is-not-mapped-header ::v-deep .v-select .v-select__selections {
			color: var(--description-text) !important;
		}
	}
	.arrow-right {
		$size: 30px;

		&::before {
			content: '';
			display: block;
			position: absolute;
			right: 0;
			top: 0;
			width: 30px;
			height: 40px;
			background-color: $dest-bg-color;
			z-index: 9;
		}
		&::after {
			content: '';
			display: block;
			position: absolute;
			top: 0;
			right: 0;
			width: 33px;
			height: 33px;
			transform: rotate(45deg) translate(-5px, 10px);
			border-color: $border-color $border-color transparent transparent;
			border-width: 1px;
			border-style: solid;
			border-radius: 5px;
			background-color: $bg-color;
			z-index: 10;
		}
	}

	&.is-merging {
		&.is-being-merged {
			border: 1px solid $blue;

			.column-destination {
				color: $blue;
				background-color: $merge-cell-bg;
			}
			.arrow-right::after {
				border-color: $blue $blue transparent transparent;
			}
			.arrow-right::before {
				background-color: $merge-cell-bg;
			}
		}
		&:not(.is-being-merged) {
			.arrow-right {
				&::before,
				&::after {
					content: none !important;
				}
			}
		}
	}

	&.is-merged-header {
		background-color: #e4f7f3;

		.arrow-right::after {
			background-color: #e4f7f3;
		}
	}

	&:hover {
		border: 1px solid $blue;
		transition: 0.4s;
	}
	&:not(:hover) {
		transition: 0.3s;
	}

	.select-container {
		position: relative;
	}
}
</style>
